import { Button, Divider, Form, Input, message, Radio, Space } from 'antd';
import React from 'react';
import { SendOutlined, ReloadOutlined, LeftOutlined, CodeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { VALIDATE_REQUIRED } from '@/config';
import { encodePhoneNumber } from '@/helpers/string';
import { apiSdk } from '@/httpClient';
import { LoginResponseModel } from '@/__generated';
import AuthenticatorEnrollment from './AuthenticatorEnrollment';

// Méthodes sans envoi : le code vient de l'application d'authentification ou d'un code de secours.
const CodeWithoutSending = ['Authenticator', 'RecoveryCode'];

type Props = {
  loginResponse: LoginResponseModel;
  on2FaVerifySuccess: (response: LoginResponseModel) => void;
};

export default function TowFactorAuth({ loginResponse, on2FaVerifySuccess }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const firstProvider = loginResponse.twoFactorProviders?.find(x => x) || 'Email';
  // Application d'authentification (méthode principale) : saisie directe du code, sans envoi.
  const [hasSendCode, setHasSendCode] = React.useState<boolean>(
    loginResponse.twoFactorProviders?.length === 1 || CodeWithoutSending.includes(firstProvider),
  );
  const [selectedProvider, setSelectedProvider] = React.useState<string>(firstProvider);
  const [enrollment, setEnrollment] = React.useState<LoginResponseModel>();

  React.useEffect(() => {
    if (loginResponse && loginResponse.twoFactorProviders?.length === 1 && !CodeWithoutSending.includes(firstProvider)) {
      sendCode(true);
    }
  }, [loginResponse]);

  const sendCode = (noMessage?: boolean) => {
    setLoading(true);
    apiSdk.AuthService.sendTwoFactorCode({
      body: {
        provider: selectedProvider,
        twoFactorToken: loginResponse.twoFactorToken || '',
      },
    })
      .then(res => {
        if (res.success) {
          setHasSendCode(true);
          if (!noMessage) {
            message.success(t('Send verifycation code successfull!'));
          }
        } else {
          message.error(t(res.message || ''));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const verifyCode = (code: string) => {
    setLoading(true);
    apiSdk.AuthService.verifyTwoFactorCode({
      body: {
        provider: selectedProvider,
        twoFactorToken: loginResponse.twoFactorToken || '',
        code: code,
      },
    })
      .then(res => {
        if (res.success) {
          if (res.authenticatorEnrollmentRequired) {
            setEnrollment(res);
          } else {
            on2FaVerifySuccess(res);
          }
        } else {
          message.error(t(res.error || ''));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  if (enrollment) {
    return <AuthenticatorEnrollment loginResponse={enrollment} onDone={() => on2FaVerifySuccess(enrollment)} />;
  }

  return (
    <div style={{ padding: 15 }}>
      <div style={{ textAlign: 'center' }}>
        <img src={require('@/assets/icons/2fa-shield.png')} style={{ width: 70 }} alt="2fa" />
        <h2>{t('loginPage_2FaAuthentication')}</h2>
      </div>
      <Divider />
      {hasSendCode ? (
        <div>
          {selectedProvider === 'Authenticator' ? <h3>{t('loginPage_AuthenticatorCodeHint')}</h3> : null}
          {selectedProvider === 'RecoveryCode' ? <h3>{t('loginPage_RecoveryCodeHint')}</h3> : null}
          {selectedProvider === 'Email' ? (
            <div>
              <h3 dangerouslySetInnerHTML={{ __html: t('loginPage_CodeHasSend2Email', { email: loginResponse.currentUser?.email }) }} />
            </div>
          ) : null}
          {selectedProvider === 'Phone' ? (
            <div>
              <h3
                dangerouslySetInnerHTML={{
                  __html: t('loginPage_CodeHasSend2Phone', { phone: encodePhoneNumber(loginResponse.currentUser?.phoneNumber) }),
                }}
              />
            </div>
          ) : null}
          <Divider />
          <Form layout="vertical" name="normal_login" onFinish={v => verifyCode(v.code)}>
            <Form.Item label={t('loginPage_YourVerifycationCode')} name="code" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
              <Input prefix={<CodeOutlined />} />
            </Form.Item>
            {CodeWithoutSending.includes(selectedProvider) ? null : (
              <div style={{ textAlign: 'end' }}>
                <Button loading={loading} type="link" onClick={() => sendCode()} style={{ paddingRight: 0 }}>
                  {t('loginPage_ReSendCode')}
                </Button>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}>
              <Button loading={loading} type="primary" htmlType="submit" className="login-form-button" icon={<ReloadOutlined />}>
                {t('loginPage_Verify')}
              </Button>
            </div>

            {(loginResponse.twoFactorProviders?.length as any) > 1 ? (
              <div style={{ textAlign: 'start' }}>
                <Button
                  loading={loading}
                  type="text"
                  onClick={() => {
                    setHasSendCode(false);
                  }}
                  icon={<LeftOutlined />}
                  style={{ paddingLeft: 0 }}
                >
                  {t('loginPage_ChangeProvider')}
                </Button>
              </div>
            ) : null}
          </Form>
        </div>
      ) : (
        <div>
          <h3>{t('loginPage_PleaseSelectProvider')}</h3>
          <Radio.Group
            onChange={e => {
              setSelectedProvider(e.target.value);
            }}
            value={selectedProvider}
          >
            <Space direction="vertical">
              {loginResponse.twoFactorProviders?.map(provider => {
                if (CodeWithoutSending.includes(provider)) {
                  return (
                    <Radio value={provider} key={provider}>
                      <b>{provider === 'Authenticator' ? t('loginPage_UseAuthenticator') : t('loginPage_UseRecoveryCode')}</b>
                    </Radio>
                  );
                } else if (provider === 'Email') {
                  return (
                    <Radio value="Email" key={provider}>
                      <Space direction="vertical" size="small">
                        <b>{t('loginPage_Email')}</b>
                        <span dangerouslySetInnerHTML={{ __html: t('loginPage_EmailSubTitle', { email: loginResponse.currentUser?.email }) }} />
                      </Space>
                    </Radio>
                  );
                } else if (provider === 'Phone') {
                  return (
                    <Radio value="Phone" key={provider}>
                      <Space direction="vertical" size="small">
                        <b>{t('loginPage_PhoneNumber')}</b>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t('loginPage_PhoneSubTitle', { phone: encodePhoneNumber(loginResponse.currentUser?.phoneNumber) }),
                          }}
                        />
                      </Space>
                    </Radio>
                  );
                }
                return <div key={provider} />;
              })}
            </Space>
          </Radio.Group>
          <Divider />
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Button
              loading={loading}
              type="primary"
              onClick={() => (CodeWithoutSending.includes(selectedProvider) ? setHasSendCode(true) : sendCode())}
              className="login-form-button"
              icon={<SendOutlined />}
            >
              {t('loginPage_Send')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
